import { useCallback, useEffect, useReducer, useRef } from 'react';
import { chooseEmailChat } from './chooseEmailChat';
import { PageChat } from './pageChat';

type VisitStatus = 'choosing' | 'failed' | 'provisional' | 'final';

type Visit = {
  visitId: number;
  status: VisitStatus;
  hasUserChosen: boolean;
  isInitialVisit: boolean;
};

type UseApplyPageChatInput = {
  conversationId: string | null;
  pageChat?: PageChat;
  onConversationSelect: (conversationId: string | null) => void;
  onNewChat: () => void;
};

export function useApplyPageChat({
  conversationId,
  pageChat,
  onConversationSelect,
  onNewChat,
}: UseApplyPageChatInput) {
  const initialVisitId = useRef<number | undefined>(undefined);
  const hasUserChosenBeforeEmail = useRef(false);
  const visit = useRef<Visit | null>(null);
  const [, rerender] = useReducer((count: number) => count + 1, 0);
  const visitId = pageChat?.visitId;

  if (visitId !== undefined && visit.current?.visitId !== visitId) {
    initialVisitId.current ??= visitId;
    const isInitialVisit =
      pageChat?.isInitialVisit ?? visitId === initialVisitId.current;
    visit.current = {
      visitId,
      status: 'choosing',
      hasUserChosen:
        isInitialVisit &&
        (hasUserChosenBeforeEmail.current || conversationId !== null),
      isInitialVisit,
    };
  }

  const isResolved = pageChat?.isResolved ?? false;
  const isFinal = pageChat?.isFinal ?? false;
  const hasFailed = pageChat?.hasFailed ?? false;
  const pageChatId = pageChat?.conversationId ?? null;

  useEffect(() => {
    const current = visit.current;
    if (!current || current.visitId !== visitId || !isResolved) return;
    if (current.status === 'final') return;

    const settle = (status: VisitStatus) => {
      current.status = status;
      rerender();
    };

    if (current.hasUserChosen) {
      settle('final');
      return;
    }
    if (hasFailed) {
      if (current.status === 'choosing') settle('failed');
      return;
    }

    const wasProvisional = current.status === 'provisional';
    if (wasProvisional && !isFinal) return;
    settle(isFinal ? 'final' : 'provisional');

    if (wasProvisional) {
      if (pageChatId && pageChatId !== conversationId) {
        onConversationSelect(pageChatId);
      }
      return;
    }

    const choice = chooseEmailChat({
      isFirstEmail: current.isInitialVisit,
      currentConversationId: conversationId,
      threadChatId: pageChatId,
    });
    if (choice.type === 'open') onConversationSelect(choice.conversationId);
    if (choice.type === 'newChat') {
      onConversationSelect(null);
      onNewChat();
    }
  });

  const noteUserSelection = useCallback(() => {
    const current = visit.current;
    if (!current) {
      hasUserChosenBeforeEmail.current = true;
      return;
    }
    if (current.status === 'final' || current.hasUserChosen) return;
    current.hasUserChosen = true;
    rerender();
  }, []);

  const isChoosingChat =
    visit.current !== null &&
    visit.current.visitId === visitId &&
    visit.current.status === 'choosing' &&
    !visit.current.hasUserChosen;

  return { isChoosingChat, noteUserSelection };
}
